/**
 * The line between two panes, which a person can drag.
 *
 * Salesforge's panes are fixed. The owner asked for these to move, twice, the
 * second time in so many words: "the panes should be all equal width and
 * adjustable". So this is ours rather than a measurement, built once and used
 * wherever a column deserves it.
 *
 * WHAT IT IS TO EACH KIND OF PERSON.
 *
 *   - With a pointer: a 12px strip centred on the pane's border, with the
 *     `col-resize` cursor. Hovered, focused or held, the hairline thickens to a
 *     3px line in the accent and a grip appears halfway down it, so the line
 *     says it can be moved before anybody tries. A double click puts the panes
 *     back: with `onReset`, every pane the caller sizes (the inbox's three go
 *     back to equal), and without it this one pane to its default.
 *   - With a keyboard: a focusable `separator` with its width as
 *     `aria-valuenow`. Left and Right move it 16px, Home puts it back the way a
 *     double click does, End takes it as far as it goes. Focus draws the same
 *     line and grip, so the place the keys act on is visible.
 *   - With a screen reader: the label names which pane it sizes, and
 *     `aria-controls` points at that pane when the caller gives its id.
 *
 * AT REST IT DRAWS NOTHING OF ITS OWN. The panes' own borders are the hairline,
 * and this lies over the border rather than beside it, so a page reads the same
 * with or without a handle until somebody reaches for one.
 *
 * THE WIDTH IS NOT OWNED HERE. The caller holds it and the handle reports moves
 * through `onResize`, which follows the pointer, and `onCommit`, which is the
 * settled value and the one worth storing.
 *
 * IT SITS ON EITHER EDGE OF ITS PARENT. The inbox puts one in a zero-width
 * spacer between two panes, where it lies over the spacer's left edge. A table
 * column puts one inside its own header cell, over the cell's right edge
 * (`edge="end"`), which is the line between that column and the next.
 *
 * Every number this decides is in `pane-width-model.ts`, where it is tested.
 */
import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { cn } from '../lib/cn'
import { dragPaneWidth, keyPaneWidth, type PaneBounds, type PaneDirection } from './pane-width-model'

export interface ResizeHandleProps {
  /** Which pane this sizes, said as a thing: "Resize the label column". */
  label: string
  width: number
  bounds: PaneBounds
  /** 1 when the pane is on the handle's left, -1 when it is on the right. */
  direction: PaneDirection
  /** Follows the pointer. */
  onResize: (width: number) => void
  /** The settled width, after a drag or a key. */
  onCommit: (width: number) => void
  /**
   * What a double click and Home do instead of putting this one pane back: the
   * inbox puts all three of its panes back to equal. Left out, they commit
   * `bounds.initial`.
   */
  onReset?: () => void
  /** Said in the tooltip after "Double click", when `onReset` puts back more than this pane. */
  resetHint?: string
  /** The id of the pane this sizes. */
  controls?: string
  /** Which edge of its parent the strip lies over: the left (`start`, the default) or the right (`end`). */
  edge?: 'start' | 'end'
  className?: string
}

const RESIZE_HANDLE_BASE_CLASS =
  'group absolute inset-y-0 z-sticky-col w-3 cursor-col-resize touch-none select-none focus-visible:outline-none'

/** The strip's own classes, exported so a test can hold the cursor and the hit area still. */
export const RESIZE_HANDLE_CLASS = `${RESIZE_HANDLE_BASE_CLASS} left-0 -translate-x-1/2`

/** The same strip, centred on its parent's right edge. */
export const RESIZE_HANDLE_END_CLASS = `${RESIZE_HANDLE_BASE_CLASS} right-0 translate-x-1/2`

/** The line inside the strip: nothing over the border at rest, a 3px accent line on hover, focus and drag. */
export function resizeLineClass(dragging: boolean): string {
  return cn(
    'pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 transition-[width,background-color] duration-150',
    dragging
      ? 'w-[3px] bg-accent'
      : 'w-px bg-transparent group-hover:w-[3px] group-hover:bg-accent group-focus-visible:w-[3px] group-focus-visible:bg-accent',
  )
}

/** The grip halfway down the line, shown with the thick line and never at rest. */
export function resizeGripClass(dragging: boolean): string {
  return cn(
    'pointer-events-none absolute left-1/2 top-1/2 flex h-8 w-3 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-[3px]',
    'rounded-full border border-line-strong bg-surface shadow-xs transition-opacity duration-150',
    dragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
  )
}

export function ResizeHandle({
  label,
  width,
  bounds,
  direction,
  onResize,
  onCommit,
  onReset,
  resetHint,
  controls,
  edge = 'start',
  className,
}: ResizeHandleProps) {
  const [dragging, setDragging] = useState(false)
  // Where the drag started, and the last width it produced, which is what the commit stores.
  const drag = useRef<{ startX: number; startWidth: number; last: number } | null>(null)
  const reset = onReset ?? (() => onCommit(bounds.initial))

  const end = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (current === null) return
    drag.current = null
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    document.body.style.removeProperty('cursor')
    document.body.style.removeProperty('user-select')
    onCommit(current.last)
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={bounds.min}
      aria-valuemax={bounds.max}
      aria-controls={controls}
      tabIndex={0}
      title={`${label}. Drag it, or use the arrow keys. Double click ${onReset ? (resetHint ?? 'puts them back') : 'puts it back'}.`}
      data-dragging={dragging ? '' : undefined}
      className={cn(edge === 'end' ? RESIZE_HANDLE_END_CLASS : RESIZE_HANDLE_CLASS, className)}
      onPointerDown={(event) => {
        if (event.button !== 0) return
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        drag.current = { startX: event.clientX, startWidth: width, last: width }
        setDragging(true)
        // The cursor and the no-select go on the body for the length of the
        // drag, so a pointer that outruns the strip keeps the resize cursor
        // and does not paint a text selection across the page.
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
      }}
      onPointerMove={(event) => {
        const current = drag.current
        if (current === null) return
        const next = dragPaneWidth(current.startWidth, current.startX, event.clientX, direction, bounds)
        if (next === current.last) return
        current.last = next
        onResize(next)
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={reset}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Home') {
          event.preventDefault()
          reset()
          return
        }
        const next = keyPaneWidth(event.key, width, direction, bounds)
        if (next === null) return
        event.preventDefault()
        onCommit(next)
      }}
    >
      <span aria-hidden="true" className={resizeLineClass(dragging)} />
      <span aria-hidden="true" className={resizeGripClass(dragging)}>
        <span className="h-[3px] w-[3px] rounded-full bg-ink-subtle" />
        <span className="h-[3px] w-[3px] rounded-full bg-ink-subtle" />
        <span className="h-[3px] w-[3px] rounded-full bg-ink-subtle" />
      </span>
    </div>
  )
}
